//MIT License

//Copyright(c) 2017 Jared Palmer http://jaredpalmer.com

//Permission is hereby granted, free of charge, to any person obtaining a copy
//of this software and associated documentation files (the "Software"), to deal
//    in the Software without restriction, including without limitation the rights
//to use, copy, modify, merge, publish, distribute, sublicense, and / or sell
//copies of the Software, and to permit persons to whom the Software is
//furnished to do so, subject to the following conditions:

//The above copyright notice and this permission notice shall be included in all
//copies or substantial portions of the Software.

//THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
//IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
//    FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.IN NO EVENT SHALL THE
//AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
//LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
//    OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
//SOFTWARE.
// https://github.com/jaredpalmer/react-simple-infinite-scroll
import { DebouncedFunc, throttle } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { bind } from '../Utils/Bind';
import { componentReaction } from '../Utils/MobxTools';

export interface IInfiniteScrollProps {
    /**
     * Callback to load more entities
     */
    onLoadMore(): void;

    /**
     * Does the resource have more entities
     */
    readonly hasMore: boolean;

    /**
     * Should show loading
     */
    readonly isLoading?: boolean;

    /**
     * Scroll threshold
     */
    readonly threshold: number;

    /**
     * Throttle raet
     */
    readonly throttle: number;

    /** Children */
    readonly children?: any;

    /** Scroll container */
    container?: HTMLElement | null;

    readonly className?: string;
}

@observer
export class InfiniteScroll extends React.Component<IInfiniteScrollProps, {}> {
    public constructor(props: IInfiniteScrollProps) {
        super(props);

        this.throttle = throttle(this.checkWindowScroll, this.props.throttle);

        const container = this.props.container ?? window;
        container.addEventListener('scroll', this.throttle);
        window.addEventListener('resize', this.throttle);
        componentReaction(this, () => this.props.isLoading, this.throttle);
    }

    public componentDidUpdate(prevProps: IInfiniteScrollProps) {
        const prev = prevProps.container ?? window;
        const current = this.props.container ?? window;

        if (prev != current) {
            prev.removeEventListener('scroll', this.throttle);
            current.addEventListener('scroll', this.throttle);
        }
    }

    public componentWillUnmount() {
        const container = this.props.container ?? window;
        container.removeEventListener('scroll', this.throttle);
        window.removeEventListener('resize', this.throttle);

        if (this.intervalHandle) window.clearInterval(this.intervalHandle);
    }

    public render() {
        return (
            <div className={this.props.className}>
                {this.props.children}
                <div id="sentinel" ref={this.setSentinel} />
            </div>
        );
    }

    @bind
    private setSentinel(r: HTMLDivElement | null) {
        this.sentinel = r;
    }

    @bind
    private checkWindowScroll() {
        if (this.props.isLoading) {
            return;
        }

        let innerHeight = window.innerHeight;

        if (this.props.container) innerHeight = this.props.container.getBoundingClientRect().bottom;

        if (
            this.props.hasMore &&
            this.sentinel &&
            this.sentinel.getBoundingClientRect().top - innerHeight < this.props.threshold
        ) {
            this.props.onLoadMore();
        }
    }

    private sentinel: HTMLDivElement | null = null;
    private intervalHandle?: number;
    private throttle: DebouncedFunc<() => void>;
}
